/**
 * PortalLayout (T-036).
 *
 * The app shell for every /portal/* route. Sidebar nav + top bar with tier
 * badge, notification bell, and user menu. Pro-only routes (Booking, Reports)
 * are hidden unless `subscribers.tier = 'pro'` (PR-46, DES-11).
 *
 * Source: app/docs/aldente/ui-structure.md §Layouts > PortalLayout and
 * §Navigation > Portal sidebar; app/docs/aldente/design-guidelines.md §2
 * (white surface), §5 (layouts at a glance — "inviting, not utilitarian").
 *
 * Desktop-first (>=1024px) per DES-11. On narrower viewports the sidebar
 * collapses to a top strip; functional, not optimized.
 *
 * The layout consumes a `PortalEntryContext` resolved by the route's
 * `beforeLoad` guard (lib/portal-guard.ts). Tier, subscriber state, and
 * operator-view flag are passed down; this component does no data fetching of
 * its own except the unread notification count for the bell badge.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Bell,
  BookOpen,
  CalendarDays,
  FileText,
  HelpCircle,
  LayoutDashboard,
  type LucideIcon,
  PlusCircle,
  Settings,
  SquareLibrary,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TierBadge } from './TierBadge';
import { countUnreadNotifications } from '@/lib/portal';

export interface PortalLayoutProps {
  children: ReactNode;
  tier: 'studio' | 'pro' | null;
  isOperator: boolean;
  subscriberStatus: 'active' | 'paused' | 'canceled' | null;
}

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  proOnly?: boolean;
}

const NAV: NavItem[] = [
  { label: 'Dashboard', to: '/portal', icon: LayoutDashboard },
  { label: 'New task', to: '/portal/tasks/new', icon: PlusCircle },
  { label: 'Context', to: '/portal/context', icon: FileText },
  { label: 'Playbook', to: '/portal/playbook', icon: SquareLibrary },
  { label: 'Booking', to: '/portal/booking', icon: CalendarDays, proOnly: true },
  { label: 'Reports', to: '/portal/reports', icon: BookOpen, proOnly: true },
  { label: 'Notifications', to: '/portal/notifications', icon: Bell },
  { label: 'Settings', to: '/portal/settings', icon: Settings },
  { label: 'Help', to: '/portal/help', icon: HelpCircle },
];

export function PortalLayout({
  children,
  tier,
  isOperator,
  subscriberStatus,
}: PortalLayoutProps) {
  // Operators viewing the portal see the Pro superset so they can QA every
  // surface. A paused/canceled subscriber still sees their nav (their data is
  // preserved per DES-07); the banner explains the state.
  const showPro = isOperator || tier === 'pro';
  const items = NAV.filter((i) => !i.proOnly || showPro);

  return (
    <div className="flex min-h-screen flex-col bg-[color:var(--color-white)] text-[color:var(--color-navy)] lg:flex-row">
      <PortalSidebar items={items} tier={tier} isOperator={isOperator} />
      <div className="flex min-w-0 flex-1 flex-col">
        <PortalTopBar />
        {subscriberStatus && subscriberStatus !== 'active' ? (
          <SubscriptionStateBanner status={subscriberStatus} />
        ) : null}
        <main className="flex-1 px-5 py-8 sm:px-8 lg:px-12">
          <div className="mx-auto w-full max-w-5xl">{children}</div>
        </main>
      </div>
    </div>
  );
}

/**
 * Bold + Rose sidebar (final design pass). Navy-deepest surface, linen-on-navy
 * logo, a CLIENT WORKSPACE block carrying the tier tag, and nav items with a
 * rose active pill. Routing, tier gating, and the nav item set are unchanged;
 * only the visual treatment is the Bold prototype Sidebar.
 */
function PortalSidebar({
  items,
  tier,
  isOperator,
}: {
  items: NavItem[];
  tier: 'studio' | 'pro' | null;
  isOperator: boolean;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <aside
      className="flex flex-col border-b border-[color:var(--color-sidebar-border)] bg-[color:var(--color-sidebar-bg)] lg:h-screen lg:w-[236px] lg:shrink-0 lg:border-b-0 lg:border-r lg:sticky lg:top-0"
      aria-label="Portal navigation"
    >
      <div className="border-b border-[color:var(--color-sidebar-border)] px-5 py-4 lg:py-5">
        <Link
          to="/portal"
          className="inline-flex items-center hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-rose)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-sidebar-bg)]"
          aria-label="Yes Please Studio portal home"
        >
          <img
            src="/logo-linen-navy.svg"
            alt="Yes Please Studio"
            className="block h-[30px] w-auto"
          />
        </Link>
      </div>

      <div className="hidden border-b border-[color:var(--color-sidebar-border)] px-[18px] py-3.5 lg:block">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-sidebar-fg)]">
          Client workspace
        </p>
        <p className="mb-2 text-sm font-semibold text-[color:var(--color-sidebar-work-fg)]">
          Your workspace
        </p>
        <TierBadge tier={tier} isOperatorView={isOperator} />
      </div>

      <nav className="flex gap-1 overflow-x-auto px-2 py-2 lg:flex-1 lg:flex-col lg:gap-px lg:overflow-y-auto lg:overflow-x-visible">
        {items.map((item) => {
          const active =
            item.to === '/portal'
              ? pathname === '/portal'
              : pathname.startsWith(item.to);
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              className={[
                'flex shrink-0 items-center gap-2.5 rounded-md px-2.5 py-2.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-rose)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-sidebar-bg)]',
                active
                  ? 'bg-[color:var(--color-sidebar-active-bg)] font-medium text-[color:var(--color-sidebar-active-fg)]'
                  : 'text-[color:var(--color-sidebar-fg)] hover:text-[color:var(--color-sidebar-work-fg)]',
              ].join(' ')}
              aria-current={active ? 'page' : undefined}
            >
              <Icon
                size={16}
                strokeWidth={1.5}
                aria-hidden="true"
                className="shrink-0 opacity-70"
              />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="hidden border-t border-[color:var(--color-sidebar-border)] px-[18px] py-3 lg:block">
        <Link
          to="/portal/settings"
          className="flex items-center gap-1.5 text-xs text-[color:var(--color-sidebar-fg)] transition-colors hover:text-[color:var(--color-sidebar-work-fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-rose)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-sidebar-bg)]"
        >
          <Settings size={13} strokeWidth={1.5} aria-hidden="true" />
          Account &amp; settings
        </Link>
      </div>
    </aside>
  );
}

function PortalTopBar() {
  return (
    <header
      className="flex h-14 items-center justify-end gap-3 border-b border-[color:var(--color-rule)] bg-[color:var(--color-white)]/95 px-5 backdrop-blur lg:h-16 lg:px-8"
      role="banner"
    >
      <NotificationBell />
      <UserMenu />
    </header>
  );
}

function NotificationBell() {
  const [unread, setUnread] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    countUnreadNotifications()
      .then((n) => {
        if (!cancelled) setUnread(n);
      })
      .catch(() => {
        if (!cancelled) setUnread(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const label =
    unread && unread > 0
      ? `Notifications, ${unread} unread`
      : 'Notifications';

  return (
    <Link
      to="/portal/notifications"
      className="relative inline-flex h-9 w-9 items-center justify-center rounded-sm text-[color:var(--color-text-mute)] hover:bg-[color:var(--color-linen)]/70 hover:text-[color:var(--color-navy)]"
      aria-label={label}
    >
      <Bell size={20} strokeWidth={1.5} aria-hidden="true" />
      {unread && unread > 0 ? (
        <span
          className="absolute -right-0.5 -top-0.5 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-[color:var(--color-rose)] px-1 text-[0.625rem] font-semibold leading-[1.1rem] text-[color:var(--color-navy)]"
          aria-hidden="true"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </Link>
  );
}

function UserMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex h-9 items-center gap-2 rounded-sm px-2 text-sm font-medium text-[color:var(--color-text-mute)] hover:bg-[color:var(--color-linen)]/70 hover:text-[color:var(--color-navy)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-rose)]"
        aria-label="Account menu"
      >
        Account
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-xs font-semibold text-[color:var(--color-text-mute)]">
          Your account
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/portal/settings">Settings</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/portal/help">Help</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          {/* /auth/sign-out is a Phase C route; consume only. */}
          <a href="/auth/sign-out">Sign out</a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SubscriptionStateBanner({
  status,
}: {
  status: 'paused' | 'canceled';
}) {
  const copy =
    status === 'paused'
      ? 'Your subscription is paused. Your context doc, playbook, and backlog are all preserved exactly as-is. Resume any time from Settings.'
      : 'Your subscription is canceled. You can still read everything here; nothing was deleted. Start again from Settings whenever you want.';
  return (
    <div
      className="border-b border-[color:var(--color-rule)] bg-[color:var(--color-linen)] px-5 py-3 text-sm text-[color:var(--color-navy)] lg:px-8"
      role="status"
    >
      {copy}{' '}
      <Link
        to="/portal/settings"
        className="font-medium underline underline-offset-4"
      >
        Open Settings
      </Link>
    </div>
  );
}
